import React, { useContext, useEffect, useState } from "react";
import { message, Spin } from "antd";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { sliceKey, reducer, actions } from "./slice";
import { authSaga } from "./saga";
import * as selectors from "./selectors";
import * as roles from "../../constants/roles";
import KeycloakService from "./keycloakService";
// Public routes that should never trigger Keycloak redirect
const PUBLIC_ROUTES = [
  "/privacy-policy",
  "/recovery",
  "/onboarding-contact",
  "/resetPassword",
  "/verifyAccount",
];

const isPublicRoute = () => {
  const path = window.location.pathname;
  return PUBLIC_ROUTES.some(
    (route) => path === route || path.startsWith(route + "/"),
  );
};

const AuthContext = React.createContext({
  login: () => {},
  logout: () => {},
  error: null,
  loginInProgress: false,
  checkingForLoggedInUser: false,
  loggedInUser: null,
  permissions: [],
  fetchLoggedInUser: () => {},
  isAuthorised: () => {},
  isCompanyAdmin: false,
  isSSO: false,
  ssoUser: null,
  isKeycloakReady: false,
  keycloakToken: null,
});

export const useAuth = () => {
  return useContext(AuthContext);
};

const AuthProvider = ({ children }) => {
  const [keycloakToken, setKeycloakToken] = useState(null);
  const [keycloakInitStarted, setKeycloakInitStarted] = useState(false);

  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: authSaga });
  const dispatch = useDispatch();

  const history = useHistory();
  const error = useSelector(selectors.selectError);
  const successMessage = useSelector(selectors.selectMessage);
  const loginInProgress = useSelector(selectors.selectLoginInProgress);
  const logoutSuccess = useSelector(selectors.selectLogoutSuccess);
  const loggedInUser = useSelector(selectors.selectLoggedInUser);
  const fetchingLoggedInUser = useSelector(
    selectors.selectFetchingLoggedInUser
  );
  const ssoUser = useSelector(selectors.selectSSOUser);
  const isSSOAuthenticated = useSelector(selectors.selectIsSSOAuthenticated);
  const isKeycloakReady = useSelector(selectors.selectIsKeycloakReady);
  const authFlow = useSelector(selectors.selectAuthFlow);
  const ssoUserFetchCompleted = useSelector(selectors.selectSSOUserFetchCompleted);
  const ssoLogoutInProgress = useSelector(selectors.selectSSOLogoutInProgress);

  // Initialize Keycloak on mount
  useEffect(() => {
    // Skip Keycloak auth initialization on public pages
    if (isPublicRoute()) {
      dispatch(actions.setAuthFlow("legacy"));
      dispatch(actions.setKeycloakReady(true));
      return;
    }

    // Prevent multiple initialization attempts
    if (keycloakInitStarted) {
      return;
    }
    setKeycloakInitStarted(true);

    const initKeycloak = async () => {
      // Skip Keycloak initialization if disabled via environment variable
      if (
        process.env.REACT_APP_ENABLE_KEYCLOAK !== "true" &&
        process.env.REACT_APP_ENABLE_KEYCLOAK_DIRECT_LOGIN !== "true"
      ) {
        console.log(
          "Keycloak disabled via REACT_APP_ENABLE_KEYCLOAK, using legacy auth",
        );
        dispatch(actions.setAuthFlow("legacy"));
        dispatch(actions.setKeycloakReady(true));
        // fetchLoggedInUser is called by the authFlow useEffect, no need to call it here
        return;
      }

      try {
        const {
          authenticated,
          keycloak,
          error: kcError,
        } = await KeycloakService.init();

        if (kcError) {
          console.error("Keycloak init error:", kcError);
          dispatch(actions.setAuthFlow("legacy"));
          dispatch(actions.setKeycloakReady(true));
          return;
        }

        // Set auth flow based on Keycloak authentication
        if (authenticated && keycloak.token) {
          dispatch(actions.setAuthFlow("keycloak"));
          setKeycloakToken(keycloak.token);
          localStorage.setItem("kc_token", keycloak.token);

          // Fetch user profile
          try {
            const response = await fetch(
              `${process.env.REACT_APP_KEYCLOAK_URL}/realms/${process.env.REACT_APP_KEYCLOAK_REALM}/protocol/openid-connect/userinfo`,
              {
                headers: { Authorization: `Bearer ${keycloak.token}` },
              },
            );
            const profile = await response.json();
            dispatch(actions.setSSOUser(profile));
            dispatch(actions.fetchLoggedInUser());
          } catch (err) {
            console.error("Failed to fetch user profile:", err);
          }
        } else {
          // Not authenticated - trigger Keycloak login
          dispatch(actions.setAuthFlow("keycloak"));
          KeycloakService.login();
        }
        dispatch(actions.setKeycloakReady(true));
      } catch (err) {
        console.error("Keycloak initialization failed:", err);
        dispatch(actions.setAuthFlow("legacy"));
        dispatch(actions.setKeycloakReady(true));
      }
    };

    initKeycloak();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = ({ username, password }) => {
    if (authFlow === "keycloak") {
      KeycloakService.login();
      return;
    }
    dispatch(actions.login({ username, password }));
  };

  const logout = () => {
    if (isSSOAuthenticated && authFlow === "keycloak") {
      dispatch(actions.setSSOLogoutInProgress(true));
      dispatch(actions.clearSSOUser());
      localStorage.removeItem("kc_token");
      localStorage.removeItem("pdfServiceToken");
      KeycloakService.logout();
    } else {
      dispatch(actions.logout());
    }
  };

  const fetchLoggedInUserAction = () => {
    if (authFlow !== "keycloak") {
      dispatch(actions.fetchLoggedInUser());
    }
  };

  useEffect(() => {
    if (error && error !== "") {
      message.error(error);
    }
  }, [error]);

  useEffect(() => {
    if (successMessage && successMessage !== "") {
      message.success(successMessage);
    }
  }, [successMessage]);

  useEffect(() => {
    if (authFlow && authFlow !== "keycloak") {
      dispatch(actions.fetchLoggedInUser());
    }
  }, [dispatch, authFlow]);

  useEffect(() => {
    if (logoutSuccess) {
      history.push("/");
    }
  }, [logoutSuccess, history]);

  const role = loggedInUser?.role;
  let isCompanyAdmin = false;
  let permissions = [];
  Object.keys(roles).forEach((key) => {
    if (role && roles[key].name === role) {
      permissions = [...roles[key].permissions];
    }
    if (role && roles.COMPANY_ADMIN.name === role) {
      isCompanyAdmin = true;
    }
  });

  const isAuthorised = (action) => {
    return permissions.indexOf(action) !== -1;
  };

  const authValues = {
    login,
    logout,
    error,
    loginInProgress,
    checkingForLoggedInUser: fetchingLoggedInUser || !isKeycloakReady,
    loggedInUser,
    permissions,
    fetchLoggedInUser: fetchLoggedInUserAction,
    isAuthorised,
    isCompanyAdmin,
    isSSO: isSSOAuthenticated,
    ssoUser,
    isKeycloakReady,
    keycloakToken,
  };

  // Show loading while Keycloak initializes, SSO user fetch is in progress, or SSO logout is in progress
  // For SSO users, wait until the user fetch completes (success or failure)
  // Skip loading for public routes
  const isLoading =
    !isPublicRoute() &&
    (!isKeycloakReady ||
      (isSSOAuthenticated && !ssoUserFetchCompleted) ||
      ssoLogoutInProgress);

  return (
    <AuthContext.Provider value={authValues}>
      {isLoading ? (
        <div
          style={{
            height: "100vh",
            width: "100vw",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spin size="large" />
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
